// frontend/src/components/Chart/WatchButton.jsx
//
// Watch/alert toggle for the current symbol. Logged-in users get Telegram
// alerts when a fresh graded entry signal fires on a watched symbol; the grade
// selector sets the minimum quality (A+ 85 / A 75 / B 65). Logged-out click
// opens the login modal — alerts are the flagship "log in for this" feature.
import React, { useEffect, useRef, useState } from 'react';
import { Bell, BellRing, Send, CheckCircle2 } from 'lucide-react';
import {
  fetchWatchlist, addWatch, removeWatch, fetchAlertSettings,
} from '../../services/api';

const WatchButton = ({ symbol, isAuthed, onRequestLogin }) => {
  const [watch, setWatch] = useState(null); // {symbol, min_score} | null
  // Telegram link state (delivery is Telegram-only): {telegramLinked, telegramConfigured, linkUrl}
  const [settings, setSettings] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!isAuthed) { setWatch(null); setSettings(null); return undefined; }
    let cancelled = false;
    fetchWatchlist()
      .then((res) => {
        if (cancelled) return;
        const hit = (res?.watchlist || []).find(
          (w) => w.symbol === symbol.toUpperCase());
        setWatch(hit || null);
      })
      .catch(() => { /* leave unwatched */ });
    fetchAlertSettings()
      .then((s) => { if (!cancelled) setSettings(s); })
      .catch(() => { /* defaults to email */ });
    return () => { cancelled = true; };
  }, [symbol, isAuthed]);

  // While a Telegram link is pending (user is watching but not linked), poll
  // until the user presses Start (backend sweeps getUpdates on each poll).
  // Stops on link or after ~3 min.
  useEffect(() => {
    const pending = !!watch && settings?.telegramConfigured && !settings?.telegramLinked;
    if (!pending) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    let tries = 0;
    pollRef.current = setInterval(() => {
      tries += 1;
      if (tries > 45) { clearInterval(pollRef.current); pollRef.current = null; return; }
      fetchAlertSettings().then(setSettings).catch(() => {});
    }, 4000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [watch, settings?.telegramLinked, settings?.telegramConfigured]);

  const toggle = () => {
    if (!isAuthed) {
      onRequestLogin('Telegram alerts when a graded signal fires on your watched stocks');
      return;
    }
    // Optimistic: flip the UI immediately, sync the server in the background
    // (revert on failure). The API goes over the tunnel — awaiting it made the
    // button feel laggy.
    const prev = watch;
    if (watch) {
      setWatch(null);
      removeWatch(symbol).catch(() => setWatch(prev));
    } else {
      const next = { symbol: symbol.toUpperCase(), min_score: 65 };
      setWatch(next);
      addWatch(symbol, 65).catch(() => setWatch(prev));
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <button
        onClick={toggle}
        title={watch
          ? `Watching ${symbol} — you'll get a Telegram alert when a fresh signal fires. Click to stop.`
          : `Watch ${symbol}: get a Telegram alert when a graded entry signal fires`}
        className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
          watch
            ? 'bg-emerald-600 text-white hover:bg-emerald-700'
            : 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
        }`}
      >
        {watch ? <BellRing size={12} /> : <Bell size={12} />}
        {watch ? 'Watching' : 'Watch'}
      </button>
      {watch && settings && (
        settings.telegramLinked ? (
          <span className="inline-flex items-center gap-0.5 text-[11px] text-emerald-600" title="Telegram connected">
            <CheckCircle2 size={12} /> linked
          </span>
        ) : settings.telegramConfigured && settings.linkUrl ? (
          <a
            href={settings.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            title="Open Telegram and press Start to connect — this page detects it automatically"
            className="inline-flex items-center gap-1 rounded-full bg-sky-50 border border-sky-200 px-2 py-0.5 text-[11px] font-medium text-sky-700 hover:bg-sky-100"
          >
            <Send size={11} /> Connect Telegram
          </a>
        ) : (
          <span className="text-[11px] text-gray-400" title="The server has no Telegram bot configured — alerts are recorded but cannot be delivered yet">
            (alert bot not available)
          </span>
        )
      )}
    </span>
  );
};

export default WatchButton;
